import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import StatusBadge from "./StatusBadge";
import ShowMonthNav from "./ShowMonthNav";
import { dayjsTz } from "@/app/_utils/dayjs";
import { ChevronRight, Drama, MapPin, Ticket } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export default function ShowList({
  shows,
  year,
  month,
  onMonthChange,
}: {
  shows: any[];
  year?: number;
  month?: number;
  onMonthChange?: (year: number, month: number) => void;
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card">
        {shows.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={Drama} text="No shows" />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {shows.map((show) => {
              const capacity = Number(show.capacity) || 0;
              const sold = Number(show.sold) || 0;
              const pct = capacity > 0 ? Math.min(100, Math.round((sold / capacity) * 100)) : 0;
              const revenue = sold * (Number(show.ticket_price) || 0);

              return (
                <div
                  key={show.id}
                  className="flex cursor-pointer items-center gap-2.5 border-b border-border px-2.5 py-3 last:border-0"
                  onClick={() => navigate(`/admin/shows/${show.id}/details`)}
                >
                  <ShowThumbnail
                    src={show.img_url}
                    alt="show"
                    style={{ height: 56, width: 46, borderRadius: 6, flexShrink: 0 }}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-[14px] font-semibold text-foreground">
                        {show.title || "N/A"}
                      </span>
                      <StatusBadge status={show.status} />
                    </div>

                    <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                      <MapPin className="size-3.5 flex-none" />
                      <span className="min-w-0 flex-1 truncate">{show.location_name || "—"}</span>
                      <span className="ml-auto flex-none whitespace-nowrap">
                        {dayjsTz(show.start).format("MMM D, YYYY")}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                      <Ticket className="size-3.5 flex-none" />
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-foreground" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="whitespace-nowrap font-medium">
                        {sold}/{capacity}
                      </span>
                      <span>·</span>
                      <span className="whitespace-nowrap">{currencyFormat.format(revenue)}</span>
                    </div>
                  </div>
                  <ChevronRight className="size-4 flex-none text-muted-foreground/50" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-[calc(100vh-180px)] min-h-[calc(100vh-180px)] flex-1 flex-col overflow-hidden rounded-md border border-border bg-card">
      {year != null && month != null && onMonthChange && (
        <ShowMonthNav year={year} month={month} onMonthChange={onMonthChange} />
      )}
      <Table className="table-fixed" containerClassName="min-h-0 flex-1 overflow-y-auto">
        <TableHeader>
          <TableRow className="sticky top-0 z-10 border-b border-border hover:bg-muted/50 [&>th]:bg-background">
            <TableHead className="h-8 w-[28%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Show
            </TableHead>
            <TableHead className="h-8 w-[14%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Venue
            </TableHead>
            <TableHead className="h-8 w-[14%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[16%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Sell-through
            </TableHead>
            <TableHead className="h-8 w-[14%] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Revenue
            </TableHead>
            <TableHead className="h-8 w-[14%] text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Date
            </TableHead>
            <TableHead className="h-8 w-10 px-0 text-center" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {shows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={7} className="h-[calc(100vh-260px)] p-0">
                <CuoEmpty icon={Drama} text="No shows" />
              </TableCell>
            </TableRow>
          ) : (
            shows.map((show) => {
            const capacity = Number(show.capacity) || 0;
            const sold = Number(show.sold) || 0;
            const pct = capacity > 0 ? Math.min(100, Math.round((sold / capacity) * 100)) : 0;
            const revenue = sold * (Number(show.ticket_price) || 0);

            return (
              <TableRow
                key={show.id}
                className="group cursor-pointer border-border/50"
                onClick={() => navigate(`/admin/shows/${show.id}/details`)}
              >
                <TableCell className="py-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <ShowThumbnail
                      src={show.img_url}
                      alt="show"
                      style={{
                        height: 32,
                        width: 32,
                        borderRadius: 6,
                        flexShrink: 0,
                      }}
                    />
                    <div className="min-w-0 leading-tight">
                      <div className="truncate text-[13.5px] text-foreground group-hover:underline">
                        {show.title || "N/A"}
                      </div>
                      {show.variant_name && (
                        <div className="truncate text-[12px] text-muted-foreground">
                          {show.variant_name}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="truncate py-2 text-muted-foreground">
                  {show.location_name || "—"}
                </TableCell>
                <TableCell className="py-2">
                  <StatusBadge status={show.status} />
                </TableCell>
                <TableCell className="py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="h-1.5 flex-1 max-w-[140px] overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-foreground"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="whitespace-nowrap text-[12px] font-medium text-muted-foreground">
                      {sold}/{capacity}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap py-2">
                  {currencyFormat.format(revenue)}
                </TableCell>
                <TableCell className="whitespace-nowrap py-2 text-right text-muted-foreground">
                  {dayjsTz(show.start).format("MMM D, YYYY")}
                </TableCell>
                <TableCell className="py-2 px-0">
                  <div className="flex items-center justify-center">
                    <ChevronRight className="size-4 text-muted-foreground/50" />
                  </div>
                </TableCell>
              </TableRow>
            );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
